// src/FileExplorerModal.js
import React, { useState, useEffect, useCallback } from "react";
import { apiFetch } from "./api";
import CommandModal from "./CommandModal"; // For issuing list_directory
import { downloadFileWithAuth } from "./downloadWithAuth";

const FileExplorerModal = ({ client, initialPath = null, onClose }) => {
  const [currentPath, setCurrentPath] = useState(initialPath);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState(initialPath ? [initialPath] : []);
  const [showCommandInput, setShowCommandInput] = useState(!initialPath); // Show if no initial path
  const [processingItems, setProcessingItems] = useState({}); // Store item paths being processed: { [path]: 'downloading' | 'zipping' }

  const handleFileOperation = useCallback(async (item, operationType) => {
    const commandType = operationType === 'download' ? 'download_file' : 'zip_and_download';
    const itemPath = item.path;

    setProcessingItems(prev => ({ ...prev, [itemPath]: operationType }));
    setError(""); // Clear previous errors

    try {
      const commandPayload = {
        client_id: client.id,
        type: commandType,
        data: itemPath, // The path to the file or folder
      };
      const issueRes = await apiFetch("/issue_command", {
        method: "POST",
        body: JSON.stringify(commandPayload),
      });

      if (issueRes.command_id) {
        // Poll for the command result to get the fileId
        let attempts = 0;
        const pollInterval = setInterval(async () => {
          attempts++;
          try {
            const resData = await apiFetch(`/command_result/${issueRes.command_id}`);
            if (resData.result && (resData.result.success !== undefined || resData.result.error)) {
              clearInterval(pollInterval);
              if (resData.result.success && resData.files && resData.files.length > 0) {
                const fileToDownload = resData.files[0];
                downloadFileWithAuth(fileToDownload.id, fileToDownload.filename);
                setError(""); // Clear error on success
              } else {
                setError(resData.result.error || `Failed to prepare ${operationType}. ${resData.error || ''}`);
              }
              setProcessingItems(prev => {
                const newState = { ...prev };
                delete newState[itemPath];
                return newState;
              });
            } else if (attempts > 10000) { // Timeout after ~20000 seconds (adjust as needed)
              clearInterval(pollInterval);
              setError(`Timeout waiting for ${operationType} preparation.`);
              setProcessingItems(prev => {
                const newState = { ...prev };
                delete newState[itemPath];
                return newState;
              });
            }
          } catch (pollError) {
            clearInterval(pollInterval);
            setError(`Error polling for ${operationType} result: ${pollError.message}`);
            setProcessingItems(prev => {
                const newState = { ...prev };
                delete newState[itemPath];
                return newState;
              });
          }
        }, 2000); // Poll every 2 seconds
      } else {
        setError(issueRes.error || `Failed to issue ${commandType} command.`);
        setProcessingItems(prev => {
            const newState = { ...prev };
            delete newState[itemPath];
            return newState;
          });
      }
    } catch (e) {
      setError(`Error during ${operationType}: ${e.message}`);
      setProcessingItems(prev => {
        const newState = { ...prev };
        delete newState[itemPath];
        return newState;
      });
    }
  }, [client.id]); // Add client.id to dependencies

  const handleDownloadFile = (item) => {
    handleFileOperation(item, 'download');
  };

  const handleZipAndDownloadFolder = (item) => {
    handleFileOperation(item, 'zip');
  };

  const fetchDirectoryContents = useCallback(async (pathToList) => {
    if (!pathToList) {
      // If no path, try to list drives first or prompt user
      try {
        const driveResult = await apiFetch("/issue_command", {
          method: "POST",
          body: JSON.stringify({ client_id: client.id, type: "list_drives" }),
        });
        if (driveResult.command_id) {
          // Poll for result - this is a simplified approach
          // In a real app, you'd have a more robust polling or WebSocket update
          setTimeout(async () => {
            const resData = await apiFetch(`/command_result/${driveResult.command_id}`);
            if (resData.result && resData.result.success) {
              // Assuming drive data is a list of strings or an object to parse
              // This part needs to be adapted based on actual list_drives output
              let driveItems = [];
              if (Array.isArray(resData.result.data)) {
                  driveItems = resData.result.data.map(d => ({ name: d, type: 'directory', path: d, isDrive: true }));
              } else if (typeof resData.result.data === 'string') {
                  // Basic parsing for df -h like output (example)
                  driveItems = resData.result.data.split('\n')
                      .filter(line => line.startsWith('/'))
                      .map(line => {
                          const parts = line.split(/\s+/);
                          return { name: parts[0], type: 'directory', path: parts[parts.length-1], isDrive: true, preview: line };
                      });
              }
              setItems(driveItems);
              setError("Select a drive to browse or enter a path.");
            } else {
              setError("Failed to list drives.");
            }
            setLoading(false);
          }, 5000); // Wait 5s for command to complete
        }
      } catch (e) {
        setError("Error listing drives: " + e.message);
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setError("");
    try {
      const commandPayload = {
        client_id: client.id,
        type: "list_directory",
        data: pathToList,
      };
      const issueRes = await apiFetch("/issue_command", {
        method: "POST",
        body: JSON.stringify(commandPayload),
      });

      if (issueRes.command_id) {
        // Simplified polling for demo; use WebSockets for real-time updates
        let attempts = 0;
        const pollInterval = setInterval(async () => {
          attempts++;
          const resData = await apiFetch(`/command_result/${issueRes.command_id}`);
          if (resData.result && (resData.result.status === 'completed' || resData.result.success !== undefined || resData.result.error)) {
            clearInterval(pollInterval);
            setLoading(false);
            if (resData.result.success) {
              setItems(resData.result.data || []);
              setCurrentPath(pathToList);
              if (!history.includes(pathToList)) {
                setHistory(prev => [...prev, pathToList]);
              }
            } else {
              setError(resData.result.error || "Failed to list directory.");
              // setItems([]); // Optionally clear items on error
            }
          } else if (attempts > 1000) { // Timeout after ~2000 seconds
            clearInterval(pollInterval);
            setLoading(false);
            setError("Timeout fetching directory contents.");
          }
        }, 2000);
      } else {
        setError(issueRes.error || "Failed to issue list_directory command.");
        setLoading(false);
      }
    } catch (e) {
      setError("Error: " + e.message);
      setLoading(false);
    }
  }, [client.id, history]);

  useEffect(() => {
    fetchDirectoryContents(currentPath);
  }, []); // Fetch on initial mount if currentPath is set

  const handleItemClick = (item) => {
    if (item.type === "directory") {
      fetchDirectoryContents(item.path);
    } else {
      // Handle file click (e.g., show details, download)
      console.log("File clicked:", item);
      // You could open ResultModal here or a specific file preview modal
    }
  };

  const handleGoUp = () => {
    if (currentPath) {
      const parentPath = currentPath.substring(0, currentPath.lastIndexOf(client.platform.toLowerCase().includes("windows") ? "\\" : "/")) || (client.platform.toLowerCase().includes("windows") ? null : "/");
      if (parentPath === currentPath) { // At root for non-windows
          fetchDirectoryContents(null); // Go to drive list
          setCurrentPath(null);
          setHistory([]);
      } else if (parentPath) {
          fetchDirectoryContents(parentPath);
      } else { // Likely at a drive root on Windows, or couldn't determine parent
          fetchDirectoryContents(null); // Go to drive list
          setCurrentPath(null);
          setHistory([]);
      }
    } else {
        // Already at drive list, do nothing or refresh drive list
        fetchDirectoryContents(null);
    }
  };
  
  const handlePathSubmit = (e) => {
    e.preventDefault();
    const newPath = e.target.elements.pathInput.value;
    if (newPath) {
        fetchDirectoryContents(newPath);
        setShowCommandInput(false);
    }
  };


  return (
    <div className="modal">
      <div className="modal-content modal-content-scrollable" style={{minWidth: '80vw', maxWidth:'90vw'}}>
        <button className="close" onClick={onClose}>×</button>
        <h3>File Explorer: {client.hostname}</h3>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', gap: '10px' }}>
            <button onClick={handleGoUp} disabled={loading || (!currentPath && items.every(it => it.isDrive))}>
            Up
            </button>
            <form onSubmit={handlePathSubmit} style={{ flexGrow: 1, display: 'flex', gap: '5px' }}>
                <input 
                    name="pathInput"
                    type="text" 
                    defaultValue={currentPath || ""} 
                    placeholder="Enter path and press Enter"
                    style={{ flexGrow: 1, padding: '8px', margin:0 }}
                    disabled={loading}
                />
                <button type="submit" disabled={loading} style={{margin:0}}>Go</button>
            </form>
        </div>

        {loading && <p>Loading...</p>}
        {error && <p className="error">{error}</p>}
        
        {!loading && !error && items.length === 0 && currentPath && <p>Directory is empty or inaccessible.</p>}
        {!loading && !error && items.length === 0 && !currentPath && <p>No drives listed or path not entered.</p>}

        <div className="command-history-table-wrapper" style={{maxHeight: '60vh'}}>
          <table>
            <thead>
              <tr>
                <th style={{width: '50px'}}></th> {/* Thumbnail */}
                <th>Name</th>
                <th>Type</th>
                <th>Size</th>
                <th>Modified</th>
                <th>Preview/Info</th>
                <th>Actions</th> {/* <--- NEW COLUMN HEADER */}
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={item.path || item.name + index}>
                  <td onClick={() => handleItemClick(item)} style={{cursor: 'pointer'}}>
                    {item.thumbnail && <img src={item.thumbnail} alt="thumb" style={{width: '48px', height: '48px', objectFit: 'contain'}} />}
                  </td>
                  <td onClick={() => handleItemClick(item)} style={{cursor: 'pointer'}}>{item.name}</td>
                  <td onClick={() => handleItemClick(item)} style={{cursor: 'pointer'}}>{item.type}{item.isDrive ? ' (Drive)' : ''}</td>
                  <td onClick={() => handleItemClick(item)} style={{cursor: 'pointer'}}>{item.size !== null && item.size !== undefined ? (item.size / 1024).toFixed(2) + " KB" : "N/A"}</td>
                  <td onClick={() => handleItemClick(item)} style={{cursor: 'pointer'}}>{item.modified_at ? new Date(item.modified_at).toLocaleString() : "N/A"}</td>
                  <td onClick={() => handleItemClick(item)} style={{cursor: 'pointer', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                    {processingItems[item.path] ? `Processing (${processingItems[item.path]})...` : (item.preview || item.error || (item.isDrive ? item.path : ''))}
                  </td>
                  <td> {/* <--- NEW ACTIONS CELL ---*/}
                    {!item.isDrive && ( // Don't show for drive roots initially
                        <>
                        {item.type === 'file' && (
                            <button
                            onClick={(e) => { e.stopPropagation(); handleDownloadFile(item); }}
                            disabled={loading || !!processingItems[item.path]}
                            style={{fontSize: '0.8em', padding: '4px 8px', marginRight: '5px'}}
                            >
                            {processingItems[item.path] === 'download' ? 'Downloading...' : 'Download'}
                            </button>
                        )}
                        {item.type === 'directory' && (
                            <button
                            onClick={(e) => { e.stopPropagation(); handleZipAndDownloadFolder(item); }}
                            disabled={loading || !!processingItems[item.path]}
                            style={{fontSize: '0.8em', padding: '4px 8px'}}
                            >
                            {processingItems[item.path] === 'zip' ? 'Zipping...' : 'Zip & Download'}
                            </button>
                        )}
                        </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FileExplorerModal;
